/**
 * Common Medications Reference
 * Sources: 
 * - Michigan LARA Quick Reference Guide (John Preston, Psy.D., ABPP) - Psychotropic medications
 * - Healthgrades Top 50 Prescribed Medications in the US - General medications
 * 
 * This data is used for autocomplete suggestions when adding medications.
 * Users can still enter custom medications not in this list.
 */

export const COMMON_MEDICATIONS = [
  // ==========================================
  // PSYCHOTROPIC MEDICATIONS
  // ==========================================
  
  // ANTIDEPRESSANTS - SSRIs
  { generic: "fluoxetine", brand: "Prozac, Sarafem", dosage: "20-80 mg", category: "Antidepressant (SSRI)" },
  { generic: "sertraline", brand: "Zoloft", dosage: "50-200 mg", category: "Antidepressant (SSRI)" },
  { generic: "paroxetine", brand: "Paxil", dosage: "20-50 mg", category: "Antidepressant (SSRI)" },
  { generic: "citalopram", brand: "Celexa", dosage: "10-40 mg", category: "Antidepressant (SSRI)" },
  { generic: "escitalopram", brand: "Lexapro", dosage: "5-20 mg", category: "Antidepressant (SSRI)" },
  { generic: "fluvoxamine", brand: "Luvox", dosage: "50-300 mg", category: "Antidepressant (SSRI)" },
  { generic: "vilazodone", brand: "Viibryd", dosage: "10-40 mg", category: "Antidepressant (SSRI)" },
  { generic: "vortioxetine", brand: "Brintellix", dosage: "10-20 mg", category: "Antidepressant" },
  
  // ANTIDEPRESSANTS - SNRIs
  { generic: "venlafaxine", brand: "Effexor", dosage: "75-350 mg", category: "Antidepressant (SNRI)" },
  { generic: "desvenlafaxine", brand: "Pristiq", dosage: "50-400 mg", category: "Antidepressant (SNRI)" },
  { generic: "duloxetine", brand: "Cymbalta", dosage: "20-80 mg", category: "Antidepressant (SNRI)" },
  { generic: "levomilnacipran", brand: "Fetzima", dosage: "40-120 mg", category: "Antidepressant (SNRI)" },
  
  // ANTIDEPRESSANTS - Tricyclics
  { generic: "imipramine", brand: "Tofranil", dosage: "150-300 mg", category: "Antidepressant (Tricyclic)" },
  { generic: "desipramine", brand: "Norpramin", dosage: "150-300 mg", category: "Antidepressant (Tricyclic)" },
  { generic: "amitriptyline", brand: "Elavil", dosage: "150-300 mg", category: "Antidepressant (Tricyclic)" },
  { generic: "nortriptyline", brand: "Aventyl, Pamelor", dosage: "75-125 mg", category: "Antidepressant (Tricyclic)" },
  { generic: "clomipramine", brand: "Anafranil", dosage: "150-250 mg", category: "Antidepressant (Tricyclic)" },
  
  // ANTIDEPRESSANTS - Other
  { generic: "bupropion", brand: "Wellbutrin, Zyban", dosage: "150-400 mg", category: "Antidepressant (NDRI)" },
  { generic: "mirtazapine", brand: "Remeron", dosage: "15-45 mg", category: "Antidepressant (Atypical)" },
  { generic: "trazodone", brand: "Desyrel, Oleptro", dosage: "150-400 mg", category: "Antidepressant (SARI)" },
  { generic: "nefazodone", brand: "Generic Only", dosage: "100-300 mg", category: "Antidepressant (SARI)" },
  
  // ANTIDEPRESSANTS - MAOIs
  { generic: "phenelzine", brand: "Nardil", dosage: "30-90 mg", category: "Antidepressant (MAOI)" },
  { generic: "tranylcypromine", brand: "Parnate", dosage: "20-60 mg", category: "Antidepressant (MAOI)" },
  { generic: "selegiline", brand: "Emsam (patch)", dosage: "6-12 mg", category: "Antidepressant (MAOI)" },
  
  // MOOD STABILIZERS / BIPOLAR
  { generic: "lithium carbonate", brand: "Eskalith, Lithonate", dosage: "600-2400 mg", category: "Mood Stabilizer" },
  { generic: "divalproex", brand: "Depakote", dosage: "750-1500 mg", category: "Mood Stabilizer" },
  { generic: "lamotrigine", brand: "Lamictal", dosage: "50-500 mg", category: "Mood Stabilizer" },
  { generic: "carbamazepine", brand: "Tegretol, Equetro", dosage: "600-1600 mg", category: "Mood Stabilizer" },
  { generic: "oxcarbazepine", brand: "Trileptal", dosage: "1200-2400 mg", category: "Mood Stabilizer" },
  { generic: "olanzapine/fluoxetine", brand: "Symbyax", dosage: "6/25-12/50 mg", category: "Mood Stabilizer (Combination)" },
  
  // ANTIPSYCHOTICS - Atypical (Second Generation)
  { generic: "risperidone", brand: "Risperdal", dosage: "4-16 mg", category: "Antipsychotic (Atypical)" },
  { generic: "paliperidone", brand: "Invega", dosage: "3-12 mg", category: "Antipsychotic (Atypical)" },
  { generic: "olanzapine", brand: "Zyprexa", dosage: "5-20 mg", category: "Antipsychotic (Atypical)" },
  { generic: "quetiapine", brand: "Seroquel", dosage: "150-600 mg", category: "Antipsychotic (Atypical)" },
  { generic: "ziprasidone", brand: "Geodon", dosage: "60-160 mg", category: "Antipsychotic (Atypical)" },
  { generic: "aripiprazole", brand: "Abilify", dosage: "15-30 mg", category: "Antipsychotic (Atypical)" },
  { generic: "clozapine", brand: "Clozaril", dosage: "300-900 mg", category: "Antipsychotic (Atypical)" },
  { generic: "iloperidone", brand: "Fanapt", dosage: "12-24 mg", category: "Antipsychotic (Atypical)" },
  { generic: "asenapine", brand: "Saphris", dosage: "10-20 mg", category: "Antipsychotic (Atypical)" },
  { generic: "lurasidone", brand: "Latuda", dosage: "40-80 mg", category: "Antipsychotic (Atypical)" },
  
  // ANTIPSYCHOTICS - Typical (First Generation)
  { generic: "haloperidol", brand: "Haldol", dosage: "2-40 mg", category: "Antipsychotic (Typical)" },
  { generic: "chlorpromazine", brand: "Thorazine", dosage: "50-800 mg", category: "Antipsychotic (Typical)" },
  { generic: "thioridazine", brand: "Mellaril", dosage: "150-800 mg", category: "Antipsychotic (Typical)" },
  { generic: "perphenazine", brand: "Trilafon", dosage: "8-60 mg", category: "Antipsychotic (Typical)" },
  { generic: "loxapine", brand: "Loxitane", dosage: "50-250 mg", category: "Antipsychotic (Typical)" },
  { generic: "trifluoperazine", brand: "Stelazine", dosage: "2-40 mg", category: "Antipsychotic (Typical)" },
  { generic: "fluphenazine", brand: "Prolixin", dosage: "3-45 mg", category: "Antipsychotic (Typical)" },
  { generic: "thiothixene", brand: "Navane", dosage: "10-60 mg", category: "Antipsychotic (Typical)" },
  { generic: "pimozide", brand: "Orap", dosage: "1-10 mg", category: "Antipsychotic (Typical)" },
  
  // ADHD / PSYCHOSTIMULANTS
  { generic: "methylphenidate", brand: "Ritalin", dosage: "5-50 mg", category: "Stimulant (ADHD)" },
  { generic: "methylphenidate ER", brand: "Concerta", dosage: "18-54 mg", category: "Stimulant (ADHD)" },
  { generic: "methylphenidate", brand: "Metadate", dosage: "5-40 mg", category: "Stimulant (ADHD)" },
  { generic: "methylphenidate", brand: "Methylin", dosage: "10-60 mg", category: "Stimulant (ADHD)" },
  { generic: "methylphenidate patch", brand: "Daytrana", dosage: "15-30 mg", category: "Stimulant (ADHD)" },
  { generic: "methylphenidate liquid", brand: "Quillivant XR", dosage: "10-60 mg", category: "Stimulant (ADHD)" },
  { generic: "dexmethylphenidate", brand: "Focalin", dosage: "5-40 mg", category: "Stimulant (ADHD)" },
  { generic: "dextroamphetamine", brand: "Dexedrine", dosage: "5-40 mg", category: "Stimulant (ADHD)" },
  { generic: "lisdexamfetamine", brand: "Vyvanse", dosage: "30-70 mg", category: "Stimulant (ADHD)" },
  { generic: "amphetamine mixed salts", brand: "Adderall", dosage: "5-40 mg", category: "Stimulant (ADHD)" },
  { generic: "atomoxetine", brand: "Strattera", dosage: "60-120 mg", category: "Non-Stimulant (ADHD)" },
  { generic: "modafinil", brand: "Provigil", dosage: "100-400 mg", category: "Wakefulness Agent" },
  { generic: "armodafinil", brand: "Nuvigil", dosage: "150-250 mg", category: "Wakefulness Agent" },
  
  // ANTI-ANXIETY - Benzodiazepines
  { generic: "diazepam", brand: "Valium", dosage: "2-10 mg", category: "Anti-Anxiety (Benzodiazepine)" },
  { generic: "lorazepam", brand: "Ativan", dosage: "0.5-2 mg", category: "Anti-Anxiety (Benzodiazepine)" },
  { generic: "alprazolam", brand: "Xanax", dosage: "0.25-2 mg", category: "Anti-Anxiety (Benzodiazepine)" },
  { generic: "clonazepam", brand: "Klonopin", dosage: "0.5-2 mg", category: "Anti-Anxiety (Benzodiazepine)" },
  { generic: "chlordiazepoxide", brand: "Librium", dosage: "10-50 mg", category: "Anti-Anxiety (Benzodiazepine)" },
  { generic: "clorazepate", brand: "Tranxene", dosage: "3.75-15 mg", category: "Anti-Anxiety (Benzodiazepine)" },
  
  // ANTI-ANXIETY - Other
  { generic: "buspirone", brand: "BuSpar", dosage: "5-20 mg", category: "Anti-Anxiety (Non-Benzo)" },
  { generic: "gabapentin", brand: "Neurontin", dosage: "200-600 mg", category: "Anti-Anxiety / Anticonvulsant" },
  { generic: "pregabalin", brand: "Lyrica", dosage: "25-450 mg", category: "Anti-Anxiety / Anticonvulsant" },
  { generic: "hydroxyzine", brand: "Atarax, Vistaril", dosage: "10-50 mg", category: "Anti-Anxiety (Antihistamine)" },
  { generic: "propranolol", brand: "Inderal", dosage: "10-80 mg", category: "Anti-Anxiety (Beta Blocker)" },
  { generic: "atenolol", brand: "Tenormin", dosage: "25-100 mg", category: "Beta Blocker" },
  { generic: "guanfacine", brand: "Tenex, Intuniv", dosage: "0.5-3 mg", category: "Anti-Anxiety (Alpha Agonist)" },
  { generic: "clonidine", brand: "Catapres, Kapvay", dosage: "0.1-0.3 mg", category: "Anti-Anxiety (Alpha Agonist)" },
  { generic: "prazosin", brand: "Minipress", dosage: "5-20 mg", category: "Anti-Anxiety (Alpha Blocker)" },
  
  // HYPNOTICS / SLEEP AIDS
  { generic: "zolpidem", brand: "Ambien", dosage: "5-10 mg", category: "Hypnotic (Sleep)" },
  { generic: "zolpidem", brand: "Intermezzo", dosage: "1.75 mg", category: "Hypnotic (Sleep)" },
  { generic: "zaleplon", brand: "Sonata", dosage: "5-10 mg", category: "Hypnotic (Sleep)" },
  { generic: "eszopiclone", brand: "Lunesta", dosage: "1-3 mg", category: "Hypnotic (Sleep)" },
  { generic: "temazepam", brand: "Restoril", dosage: "15-30 mg", category: "Hypnotic (Benzodiazepine)" },
  { generic: "triazolam", brand: "Halcion", dosage: "0.25-0.5 mg", category: "Hypnotic (Benzodiazepine)" },
  { generic: "ramelteon", brand: "Rozerem", dosage: "4-16 mg", category: "Hypnotic (Melatonin Agonist)" },
  { generic: "doxepin", brand: "Silenor", dosage: "3-6 mg", category: "Hypnotic (Antidepressant)" },
  { generic: "diphenhydramine", brand: "Benadryl", dosage: "25-100 mg", category: "Hypnotic (OTC Antihistamine)" },

  // ==========================================
  // TOP 50 GENERAL MEDICATIONS (Non-Psychotropic)
  // ==========================================
  
  // CARDIOVASCULAR - Statins (Cholesterol)
  { generic: "atorvastatin", brand: "Lipitor", dosage: "10-80 mg", category: "Statin (Cholesterol)" },
  { generic: "simvastatin", brand: "Zocor", dosage: "5-40 mg", category: "Statin (Cholesterol)" },
  { generic: "rosuvastatin", brand: "Crestor", dosage: "5-40 mg", category: "Statin (Cholesterol)" },
  { generic: "pravastatin", brand: "Pravachol", dosage: "10-80 mg", category: "Statin (Cholesterol)" },
  
  // CARDIOVASCULAR - Blood Pressure
  { generic: "lisinopril", brand: "Prinivil, Zestril", dosage: "5-40 mg", category: "ACE Inhibitor (Blood Pressure)" },
  { generic: "amlodipine", brand: "Norvasc", dosage: "2.5-10 mg", category: "Calcium Channel Blocker (Blood Pressure)" },
  { generic: "metoprolol", brand: "Lopressor, Toprol XL", dosage: "25-200 mg", category: "Beta Blocker (Blood Pressure)" },
  { generic: "losartan", brand: "Cozaar", dosage: "25-100 mg", category: "ARB (Blood Pressure)" },
  { generic: "hydrochlorothiazide", brand: "Microzide", dosage: "12.5-50 mg", category: "Diuretic (Blood Pressure)" },
  { generic: "furosemide", brand: "Lasix", dosage: "20-80 mg", category: "Loop Diuretic" },
  { generic: "carvedilol", brand: "Coreg", dosage: "3.125-25 mg", category: "Beta Blocker (Heart Failure)" },
  { generic: "lisinopril/hydrochlorothiazide", brand: "Prinzide, Zestoretic", dosage: "10/12.5-20/25 mg", category: "ACE Inhibitor/Diuretic Combo" },
  
  // CARDIOVASCULAR - Anticoagulants
  { generic: "clopidogrel", brand: "Plavix", dosage: "75 mg", category: "Antiplatelet (Blood Thinner)" },
  { generic: "apixaban", brand: "Eliquis", dosage: "2.5-5 mg", category: "Anticoagulant (Blood Thinner)" },
  { generic: "aspirin", brand: "Various", dosage: "81-325 mg", category: "Antiplatelet / Pain Reliever" },
  
  // DIABETES
  { generic: "metformin", brand: "Glucophage", dosage: "500-2000 mg", category: "Diabetes (Biguanide)" },
  { generic: "insulin glargine", brand: "Lantus, Toujeo", dosage: "10-80 units", category: "Diabetes (Long-acting Insulin)" },
  { generic: "glipizide", brand: "Glucotrol", dosage: "5-20 mg", category: "Diabetes (Sulfonylurea)" },
  
  // THYROID
  { generic: "levothyroxine", brand: "Synthroid, Levoxyl", dosage: "25-200 mcg", category: "Thyroid Hormone" },
  
  // RESPIRATORY
  { generic: "albuterol", brand: "Ventolin, ProAir", dosage: "90 mcg/puff", category: "Bronchodilator (Asthma/COPD)" },
  { generic: "montelukast", brand: "Singulair", dosage: "10 mg", category: "Leukotriene Inhibitor (Asthma/Allergies)" },
  { generic: "fluticasone", brand: "Flonase, Flovent", dosage: "50-500 mcg", category: "Corticosteroid (Respiratory)" },
  
  // GASTROINTESTINAL
  { generic: "omeprazole", brand: "Prilosec", dosage: "20-40 mg", category: "PPI (Acid Reflux/GERD)" },
  { generic: "pantoprazole", brand: "Protonix", dosage: "20-40 mg", category: "PPI (Acid Reflux/GERD)" },
  
  // PAIN / ANTI-INFLAMMATORY
  { generic: "hydrocodone/acetaminophen", brand: "Vicodin, Norco", dosage: "5/325-10/325 mg", category: "Opioid Analgesic (Pain)" },
  { generic: "tramadol", brand: "Ultram", dosage: "50-100 mg", category: "Opioid Analgesic (Pain)" },
  { generic: "meloxicam", brand: "Mobic", dosage: "7.5-15 mg", category: "NSAID (Pain/Inflammation)" },
  { generic: "ibuprofen", brand: "Advil, Motrin", dosage: "200-800 mg", category: "NSAID (Pain/Inflammation)" },
  { generic: "prednisone", brand: "Deltasone", dosage: "5-60 mg", category: "Corticosteroid (Inflammation)" },
  { generic: "cyclobenzaprine", brand: "Flexeril", dosage: "5-10 mg", category: "Muscle Relaxant" },
  
  // ==========================================
  // NON-PSYCHOTROPIC / GENERAL MEDICATIONS
  // ==========================================
  
  // PAIN RELIEF / NSAIDs (OTC)
  { generic: "acetaminophen", brand: "Tylenol", dosage: "325-1000 mg", category: "Pain Relief (OTC)" },
  { generic: "ibuprofen", brand: "Advil, Motrin", dosage: "200-800 mg", category: "Pain Relief/NSAID (OTC)" },
  { generic: "aspirin", brand: "Bayer, Ecotrin", dosage: "81-650 mg", category: "Pain Relief/NSAID (OTC)" },
  { generic: "naproxen", brand: "Aleve", dosage: "220-500 mg", category: "Pain Relief/NSAID (OTC)" },
  { generic: "celecoxib", brand: "Celebrex", dosage: "100-200 mg", category: "Pain Relief/NSAID" },
  { generic: "meloxicam", brand: "Mobic", dosage: "7.5-15 mg", category: "Pain Relief/NSAID" },
  { generic: "diclofenac", brand: "Voltaren", dosage: "50-150 mg", category: "Pain Relief/NSAID" },
  
  // ALLERGY / ANTIHISTAMINES (OTC)
  { generic: "diphenhydramine", brand: "Benadryl", dosage: "25-50 mg", category: "Antihistamine (OTC)" },
  { generic: "loratadine", brand: "Claritin", dosage: "10 mg", category: "Antihistamine (OTC)" },
  { generic: "cetirizine", brand: "Zyrtec", dosage: "5-10 mg", category: "Antihistamine (OTC)" },
  { generic: "fexofenadine", brand: "Allegra", dosage: "60-180 mg", category: "Antihistamine (OTC)" },
  { generic: "montelukast", brand: "Singulair", dosage: "10 mg", category: "Allergy/Asthma" },
  
  // GI / STOMACH (OTC & Rx)
  { generic: "omeprazole", brand: "Prilosec", dosage: "20-40 mg", category: "Proton Pump Inhibitor (OTC)" },
  { generic: "pantoprazole", brand: "Protonix", dosage: "20-40 mg", category: "Proton Pump Inhibitor" },
  { generic: "esomeprazole", brand: "Nexium", dosage: "20-40 mg", category: "Proton Pump Inhibitor (OTC)" },
  { generic: "famotidine", brand: "Pepcid", dosage: "20-40 mg", category: "H2 Blocker (OTC)" },
  { generic: "calcium carbonate", brand: "Tums", dosage: "500-1500 mg", category: "Antacid (OTC)" },
  { generic: "bismuth subsalicylate", brand: "Pepto-Bismol", dosage: "262-524 mg", category: "Antidiarrheal (OTC)" },
  { generic: "loperamide", brand: "Imodium", dosage: "2-4 mg", category: "Antidiarrheal (OTC)" },
  { generic: "docusate", brand: "Colace", dosage: "100-300 mg", category: "Stool Softener (OTC)" },
  { generic: "polyethylene glycol", brand: "Miralax", dosage: "17 g", category: "Laxative (OTC)" },
  { generic: "senna", brand: "Senokot", dosage: "8.6-17.2 mg", category: "Laxative (OTC)" },
  { generic: "ondansetron", brand: "Zofran", dosage: "4-8 mg", category: "Anti-nausea" },
  { generic: "promethazine", brand: "Phenergan", dosage: "12.5-25 mg", category: "Anti-nausea" },
  
  // BLOOD PRESSURE / CARDIOVASCULAR
  { generic: "lisinopril", brand: "Zestril, Prinivil", dosage: "5-40 mg", category: "ACE Inhibitor" },
  { generic: "losartan", brand: "Cozaar", dosage: "25-100 mg", category: "ARB" },
  { generic: "amlodipine", brand: "Norvasc", dosage: "2.5-10 mg", category: "Calcium Channel Blocker" },
  { generic: "metoprolol", brand: "Lopressor, Toprol XL", dosage: "25-200 mg", category: "Beta Blocker" },
  { generic: "atenolol", brand: "Tenormin", dosage: "25-100 mg", category: "Beta Blocker" },
  { generic: "carvedilol", brand: "Coreg", dosage: "6.25-25 mg", category: "Beta Blocker" },
  { generic: "hydrochlorothiazide", brand: "HCTZ, Microzide", dosage: "12.5-50 mg", category: "Diuretic" },
  { generic: "furosemide", brand: "Lasix", dosage: "20-80 mg", category: "Diuretic" },
  { generic: "spironolactone", brand: "Aldactone", dosage: "25-100 mg", category: "Diuretic" },
  
  // CHOLESTEROL
  { generic: "atorvastatin", brand: "Lipitor", dosage: "10-80 mg", category: "Statin" },
  { generic: "simvastatin", brand: "Zocor", dosage: "10-40 mg", category: "Statin" },
  { generic: "rosuvastatin", brand: "Crestor", dosage: "5-40 mg", category: "Statin" },
  { generic: "pravastatin", brand: "Pravachol", dosage: "10-80 mg", category: "Statin" },
  { generic: "ezetimibe", brand: "Zetia", dosage: "10 mg", category: "Cholesterol Absorption Inhibitor" },
  
  // DIABETES
  { generic: "metformin", brand: "Glucophage", dosage: "500-2000 mg", category: "Diabetes" },
  { generic: "glipizide", brand: "Glucotrol", dosage: "5-20 mg", category: "Diabetes" },
  { generic: "glyburide", brand: "Diabeta", dosage: "1.25-10 mg", category: "Diabetes" },
  { generic: "insulin (various)", brand: "Humalog, Novolog, Lantus", dosage: "Varies", category: "Diabetes" },
  { generic: "sitagliptin", brand: "Januvia", dosage: "100 mg", category: "Diabetes" },
  { generic: "empagliflozin", brand: "Jardiance", dosage: "10-25 mg", category: "Diabetes" },
  { generic: "liraglutide", brand: "Victoza", dosage: "0.6-1.8 mg", category: "Diabetes" },
  
  // THYROID
  { generic: "levothyroxine", brand: "Synthroid, Levoxyl", dosage: "25-200 mcg", category: "Thyroid" },
  { generic: "liothyronine", brand: "Cytomel", dosage: "5-50 mcg", category: "Thyroid" },
  { generic: "methimazole", brand: "Tapazole", dosage: "5-30 mg", category: "Thyroid" },
  
  // RESPIRATORY / COLD & FLU
  { generic: "albuterol", brand: "ProAir, Ventolin", dosage: "90 mcg/puff", category: "Bronchodilator" },
  { generic: "fluticasone", brand: "Flonase", dosage: "50 mcg/spray", category: "Nasal Steroid (OTC)" },
  { generic: "budesonide", brand: "Rhinocort", dosage: "32 mcg/spray", category: "Nasal Steroid (OTC)" },
  { generic: "pseudoephedrine", brand: "Sudafed", dosage: "30-60 mg", category: "Decongestant (OTC)" },
  { generic: "guaifenesin", brand: "Mucinex", dosage: "200-400 mg", category: "Expectorant (OTC)" },
  { generic: "dextromethorphan", brand: "Robitussin DM", dosage: "10-30 mg", category: "Cough Suppressant (OTC)" },
  { generic: "benzonatate", brand: "Tessalon", dosage: "100-200 mg", category: "Cough Suppressant" },
  { generic: "prednisone", brand: "Deltasone", dosage: "5-60 mg", category: "Corticosteroid" },
  { generic: "methylprednisolone", brand: "Medrol", dosage: "4-48 mg", category: "Corticosteroid" },
  
  // ANTIBIOTICS
  { generic: "amoxicillin", brand: "Amoxil", dosage: "250-500 mg", category: "Antibiotic" },
  { generic: "amoxicillin-clavulanate", brand: "Augmentin", dosage: "500-875 mg", category: "Antibiotic" },
  { generic: "azithromycin", brand: "Zithromax, Z-Pack", dosage: "250-500 mg", category: "Antibiotic" },
  { generic: "ciprofloxacin", brand: "Cipro", dosage: "250-750 mg", category: "Antibiotic" },
  { generic: "levofloxacin", brand: "Levaquin", dosage: "250-750 mg", category: "Antibiotic" },
  { generic: "doxycycline", brand: "Vibramycin", dosage: "100 mg", category: "Antibiotic" },
  { generic: "cephalexin", brand: "Keflex", dosage: "250-500 mg", category: "Antibiotic" },
  { generic: "sulfamethoxazole-trimethoprim", brand: "Bactrim", dosage: "800/160 mg", category: "Antibiotic" },
  { generic: "metronidazole", brand: "Flagyl", dosage: "250-500 mg", category: "Antibiotic" },
  { generic: "nitrofurantoin", brand: "Macrobid", dosage: "100 mg", category: "Antibiotic" },
  
  // MUSCLE RELAXANTS
  { generic: "cyclobenzaprine", brand: "Flexeril", dosage: "5-10 mg", category: "Muscle Relaxant" },
  { generic: "methocarbamol", brand: "Robaxin", dosage: "500-1500 mg", category: "Muscle Relaxant" },
  { generic: "baclofen", brand: "Lioresal", dosage: "10-80 mg", category: "Muscle Relaxant" },
  { generic: "tizanidine", brand: "Zanaflex", dosage: "2-8 mg", category: "Muscle Relaxant" },
  
  // SLEEP AIDS (OTC)
  { generic: "melatonin", brand: "Various", dosage: "1-10 mg", category: "Sleep Aid (OTC)" },
  { generic: "doxylamine", brand: "Unisom", dosage: "25 mg", category: "Sleep Aid (OTC)" },
  
  // VITAMINS & SUPPLEMENTS (OTC)
  { generic: "vitamin D3", brand: "Various", dosage: "1000-5000 IU", category: "Vitamin/Supplement (OTC)" },
  { generic: "vitamin B12", brand: "Various", dosage: "500-2500 mcg", category: "Vitamin/Supplement (OTC)" },
  { generic: "folic acid", brand: "Various", dosage: "400-1000 mcg", category: "Vitamin/Supplement (OTC)" },
  { generic: "ferrous sulfate", brand: "Iron", dosage: "325 mg", category: "Vitamin/Supplement (OTC)" },
  { generic: "calcium + vitamin D", brand: "Caltrate, Os-Cal", dosage: "600/400 IU", category: "Vitamin/Supplement (OTC)" },
  { generic: "fish oil / omega-3", brand: "Various", dosage: "1000-2000 mg", category: "Vitamin/Supplement (OTC)" },
  { generic: "multivitamin", brand: "Various", dosage: "1 tablet", category: "Vitamin/Supplement (OTC)" },
  { generic: "magnesium", brand: "Various", dosage: "250-500 mg", category: "Vitamin/Supplement (OTC)" },
  { generic: "potassium chloride", brand: "K-Dur, Klor-Con", dosage: "10-20 mEq", category: "Electrolyte Replacement" },
  { generic: "ergocalciferol", brand: "Drisdol", dosage: "50,000 IU", category: "Vitamin D (Rx Strength)" },
  
  // BLOOD THINNERS
  { generic: "warfarin", brand: "Coumadin", dosage: "1-10 mg", category: "Anticoagulant" },
  { generic: "apixaban", brand: "Eliquis", dosage: "2.5-5 mg", category: "Anticoagulant" },
  { generic: "rivaroxaban", brand: "Xarelto", dosage: "10-20 mg", category: "Anticoagulant" },
  { generic: "clopidogrel", brand: "Plavix", dosage: "75 mg", category: "Antiplatelet" },
  
  // SEIZURE / NERVE PAIN (non-psych uses)
  { generic: "gabapentin", brand: "Neurontin", dosage: "300-3600 mg", category: "Anticonvulsant/Nerve Pain" },
  { generic: "pregabalin", brand: "Lyrica", dosage: "75-300 mg", category: "Anticonvulsant/Nerve Pain" },
  
  // EYE CARE
  { generic: "artificial tears", brand: "Systane, Refresh", dosage: "As needed", category: "Eye Care (OTC)" },
  { generic: "latanoprost", brand: "Xalatan", dosage: "0.005%", category: "Glaucoma" },
  
  // SKIN / TOPICAL (OTC & Rx)
  { generic: "hydrocortisone cream", brand: "Cortaid", dosage: "0.5-1%", category: "Topical Steroid (OTC)" },
  { generic: "triamcinolone cream", brand: "Kenalog", dosage: "0.1%", category: "Topical Steroid" },
  { generic: "clotrimazole", brand: "Lotrimin", dosage: "1%", category: "Antifungal (OTC)" },
  { generic: "terbinafine", brand: "Lamisil", dosage: "1%", category: "Antifungal (OTC)" },
  
  // UROLOGY
  { generic: "tamsulosin", brand: "Flomax", dosage: "0.4 mg", category: "Prostate/BPH" },
  { generic: "finasteride", brand: "Proscar", dosage: "5 mg", category: "Prostate/BPH" },
  { generic: "sildenafil", brand: "Viagra", dosage: "25-100 mg", category: "Erectile Dysfunction" },
  { generic: "tadalafil", brand: "Cialis", dosage: "5-20 mg", category: "Erectile Dysfunction" },
  
  // HORMONES / WOMEN'S HEALTH
  { generic: "estradiol", brand: "Estrace", dosage: "0.5-2 mg", category: "Hormone" },
  { generic: "progesterone", brand: "Prometrium", dosage: "100-200 mg", category: "Hormone" },
  { generic: "ethinyl estradiol/norethindrone", brand: "Various", dosage: "Varies", category: "Oral Contraceptive" },
  
  // GOUT
  { generic: "allopurinol", brand: "Zyloprim", dosage: "100-300 mg", category: "Gout Treatment" },
  { generic: "colchicine", brand: "Colcrys", dosage: "0.6 mg", category: "Gout Treatment" },
];

/**
 * Get unique categories from the medications list
 */
export const MEDICATION_CATEGORIES = [...new Set(COMMON_MEDICATIONS.map(m => m.category))].sort();

/**
 * Search medications by name (generic or brand)
 * @param {string} query - Search term
 * @param {number} limit - Max results to return
 * @returns {Array} Matching medications
 */
export const searchMedications = (query, limit = 10) => {
  if (!query || query.length < 2) return [];
  
  const normalizedQuery = query.toLowerCase().trim();
  
  // Deduplicate results by generic name (keep first match)
  const seen = new Set();
  const results = [];
  
  for (const med of COMMON_MEDICATIONS) {
    if (seen.has(med.generic.toLowerCase())) continue;
    
    if (med.generic.toLowerCase().includes(normalizedQuery) ||
        med.brand.toLowerCase().includes(normalizedQuery)) {
      seen.add(med.generic.toLowerCase());
      results.push(med);
      if (results.length >= limit) break;
    }
  }
  
  return results;
};

export default COMMON_MEDICATIONS;
